import React, { Fragment, useEffect, useMemo, useState } from "react";
import Axios from "axios";
import { Animate, Card, Loader, Message, Row, useAPI } from "components/lib";
import { applyFormTheme, useFormBrandColor } from "components/survey/formTheme";
import { createVeriffFrame, MESSAGES } from "@veriff/incontext-sdk";
import { useParams } from "react-router-dom";
import { safeRedirectUrl } from "./safe-redirect";
import { StylesManager, Model } from "survey-core";
import { Survey } from "survey-react-ui";
import "survey-core/defaultV2.min.css";
const {
  tfnQuestionNamesFromSchema,
  collectTfnSubmitErrors,
} = require("./australian-identifiers");

StylesManager.applyTheme("defaultV2");

function getFrameUrl(verification) {
  return (
    verification?.session_url ||
    `https://magic.veriff.me/v/${verification?.session_token}`
  );
}

function getRedirectPath(verification, params) {
  if (
    verification?.response_id &&
    verification?.response_type === "post_verification_form"
  ) {
    return `/public/response/${verification.response_id}`;
  }

  // The account's redirect, only when it is an http(s) URL or a same-origin
  // path: a javascript: value would run script in this page's origin.
  const redirectUrl = safeRedirectUrl(verification?.redirectUrl);
  if (redirectUrl) {
    return redirectUrl;
  }

  return `/finish?id=${params.id}`;
}

function buildSurveyModel(schema, theme) {
  if (!schema || typeof schema !== "object") return null;
  const model = new Model(schema);
  model.showCompletedPage = false;
  return applyFormTheme(model, theme);
}

function restoreSurveyForRetry(model) {
  if (!model || typeof model.clear !== "function") return;
  model.clear(false, false);
}

function FlowCard({ title, children }) {
  return (
    <div className="w-[96vw] max-w-[980px] px-2 sm:px-4">
      <Card title={title} className="w-full mx-auto">
        <div className="w-full overflow-x-auto">{children}</div>
      </Card>
    </div>
  );
}

/*
 * The submit banner. `details` is the route's per-field `errors` array — a
 * human-readable question path plus a fixed message, never a value — which the
 * Tranche 2 intake route returns alongside "Invalid TFN details". Rendering only
 * the message left a client on a public link told that something in the form was
 * wrong with no indication of which row, and no staff member to ask.
 */
function SubmitError({ message, details }) {
  return (
    <Message type="warning">
      {message}
      {details.length > 0 && (
        <ul className="mt-2 pl-5">
          {details.map((detail, index) => (
            <li key={`${detail.label}-${index}`}>
              {detail.label}: {detail.message}
            </li>
          ))}
        </ul>
      )}
    </Message>
  );
}

export function Tranche2Verification() {
  const params = useParams();
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitErrorDetails, setSubmitErrorDetails] = useState([]);

  const verification = useAPI(`/api/public/verification/${params.id}`, "get");

  const verificationData = verification.data;
  const isTranche2Verification = verificationData?.type?.includes("tranche2_v1");
  const tranche2Flow = verificationData?.tranche2_flow;
  const currentStep = tranche2Flow?.current_step;

  useEffect(() => {
    setSubmitError(null);
    setSubmitErrorDetails([]);
  }, [currentStep]);

  useEffect(() => {
    if (!verification.loading && verificationData && !isTranche2Verification) {
      window.location.replace(`/public/verification/${params.id}`);
    }
  }, [verification.loading, verificationData, isTranche2Verification, params.id]);

  useEffect(() => {
    // request has been cancelled — send to the finished screen (with a cancelled
    // banner) instead of loading the form
    if (!verification.loading && verificationData?.cancelled) {
      window.location.replace(`/finish?cancelled=true`);
    }
  }, [verification.loading, verificationData]);

  useEffect(() => {
    if (!verificationData || !isTranche2Verification || currentStep !== "biometric") {
      return;
    }

    createVeriffFrame({
      url: getFrameUrl(verificationData),
      onReload: () => {
        window.location.reload();
      },
      onEvent: function (msg) {
        if (msg === MESSAGES.CANCELED) {
          window.location.replace("/finish");
          return;
        }

        if (msg === MESSAGES.SUBMITTED || msg === MESSAGES.FINISHED) {
          window.location.replace(getRedirectPath(verificationData, params));
        }
      },
    });
  }, [verificationData, isTranche2Verification, currentStep, params]);

  const consentSurvey = useMemo(
    () =>
      buildSurveyModel(
        tranche2Flow?.consent_form?.schema,
        tranche2Flow?.consent_form?.theme
      ),
    [tranche2Flow?.consent_form?.schema, tranche2Flow?.consent_form?.theme]
  );
  const intakeSurvey = useMemo(
    () =>
      buildSurveyModel(
        tranche2Flow?.intake_form?.schema,
        tranche2Flow?.intake_form?.theme
      ),
    [tranche2Flow?.intake_form?.schema, tranche2Flow?.intake_form?.theme]
  );

  // one colour for the whole journey, resolved server-side (R8)
  useFormBrandColor(tranche2Flow?.brand_theme, !!verificationData);

  // feature 008 (R8): which intake questions hold a TFN, read from the same form
  // schema the route audits so the browser and the route cannot disagree about
  // which answers to check. The `tfn: true` flag lives on the question
  // definition, so a firm may ask for one under any name it likes.
  const tfnQuestionNames = useMemo(
    () => tfnQuestionNamesFromSchema(tranche2Flow?.intake_form?.schema),
    [tranche2Flow?.intake_form?.schema]
  );

  // Catch a mistyped TFN at the field, while the person who can correct a digit
  // is still on the page. Left to the XPM write path it lands hours later as a
  // failure marker on the risk assessment that nobody reads.
  //
  // The answer is WALKED rather than tested as a scalar. A matrix or dynamic
  // panel holds its tax file numbers in rows, so the flagged question is a row
  // column and the container — the question this handler is called for — is not
  // in tfnQuestionNames at all. Keyed on the container name, this check skipped
  // the whole shape and left the route to reject the submission with a message
  // the client could not act on.
  //
  // collectTfnSubmitErrors rather than validateTfn: a numeric-typed question is
  // the firm's bug, and blocking on it here would dead-end every one of that
  // firm's clients on a form only the firm can fix. The route takes the same
  // view and raises an audit event to the firm instead.
  const validateIntakeTfn = (sender, options) => {
    const name = options?.question?.name;
    if (!name) return;

    const errors = collectTfnSubmitErrors(
      { [name]: options.value },
      { fieldNames: tfnQuestionNames }
    );
    if (errors.length === 0) return;

    // The label is the path from this question down; the question's own name is
    // already on the field, so only the part below it is worth repeating.
    const prefix = `${name} / `;
    options.error = errors
      .map((error) =>
        error.label === name
          ? error.message
          : `${error.message} (${
              error.label.startsWith(prefix)
                ? error.label.slice(prefix.length)
                : error.label
            })`
      )
      .join("; ");
  };

  const submitConsent = async (sender) => {
    const payload = sender?.data || {};
    setSubmitting(true);
    setSubmitError(null);
    setSubmitErrorDetails([]);
    try {
      await Axios.post(`/api/public/verification/${params.id}/tranche2/consent`, payload);
      window.location.replace(
        `/public/tranche2/verification/${params.id}?step=intake&t=${Date.now()}`
      );
      return;
    } catch (error) {
      setSubmitError(
        error?.response?.data?.message || "Unable to submit consent right now."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const submitIntake = async (sender) => {
    const payload = sender?.data || {};
    setSubmitting(true);
    setSubmitError(null);
    setSubmitErrorDetails([]);
    try {
      await Axios.post(`/api/public/verification/${params.id}/tranche2/intake`, payload);
      window.location.replace(
        `/public/tranche2/verification/${params.id}?step=biometric&t=${Date.now()}`
      );
      return;
    } catch (error) {
      restoreSurveyForRetry(sender);
      // The route answers a mistyped TFN with { message, errors: [{ label,
      // message }] }, where the label is the question path ("tfn_details / #2 /
      // tfn"). Dropping `errors` is what made a matrix-shaped intake form a dead
      // end: "Invalid TFN details" and nothing else, on a link the client is
      // filling in unsupervised.
      const failure = error?.response?.data;
      setSubmitError(failure?.message || "Unable to submit intake right now.");
      setSubmitErrorDetails(Array.isArray(failure?.errors) ? failure.errors : []);
    } finally {
      setSubmitting(false);
    }
  };

  const uploadIntakeFile = async (sender, options) => {
    try {
      const uploaded = await Promise.all(
        options.files.map(async (file) => {
          const formData = new FormData();
          formData.append("file", file);
          formData.append("field_name", options.name);
          formData.append("file_name", file.name);

          const { data } = await Axios.post(
            `/api/public/verification/${params.id}/tranche2/intake/file`,
            formData
          );

          return {
            file: file,
            name: data.name,
            type: file.type,
            content: data.url,
            key: data.key,
            id: file.id,
          };
        })
      );

      options.callback("success", uploaded);
    } catch (error) {
      options.callback(
        "error",
        error?.response?.data?.message || "Unable to upload file right now."
      );
    }
  };

  if (verification.loading || submitting || isTranche2Verification === undefined) {
    return <Loader />;
  }

  if (verificationData === null || verificationData === undefined) {
    return (
      <Animate type="pop">
        <Row
          color="brand"
          title="Your session is invalid, please contact your administrator!"
        />
      </Animate>
    );
  }

  // request has been cancelled — redirect to the finished screen (handled in the
  // effect above); show a loader while the browser navigates
  if (verificationData.cancelled) {
    return <Loader />;
  }

  if (!isTranche2Verification) {
    return <Loader />;
  }

  if (tranche2Flow?.enabled !== true) {
    return (
      <Animate type="pop">
        <Row
          color="brand"
          title="Tranche 2 flow is not enabled for this session."
        />
      </Animate>
    );
  }

  if (currentStep === "blocked") {
    return (
      <Animate type="pop">
        <Row
          color="brand"
          title={
            tranche2Flow?.blocked_reason ||
            "Your consent was not provided. Please contact your administrator."
          }
        />
      </Animate>
    );
  }

  if (currentStep === "consent") {
    if (!consentSurvey) {
      return (
        <Animate type="pop">
          <Row
            color="brand"
            title="Consent form is unavailable. Please contact your administrator."
          />
        </Animate>
      );
    }

    consentSurvey.onComplete.clear();
    consentSurvey.onComplete.add(submitConsent);

    return (
      <Fragment>
        <FlowCard title={tranche2Flow?.consent_form?.name || "Consent"}>
          {submitError && (
            <SubmitError message={submitError} details={submitErrorDetails} />
          )}
          <Survey model={consentSurvey} />
        </FlowCard>
      </Fragment>
    );
  }

  if (currentStep === "intake") {
    if (!intakeSurvey) {
      return (
        <Animate type="pop">
          <Row
            color="brand"
            title="Intake form is unavailable. Please contact your administrator."
          />
        </Animate>
      );
    }

    intakeSurvey.onComplete.clear();
    intakeSurvey.onComplete.add(submitIntake);
    intakeSurvey.onUploadFiles.clear();
    intakeSurvey.onUploadFiles.add(uploadIntakeFile);
    intakeSurvey.onValidateQuestion.clear();
    intakeSurvey.onValidateQuestion.add(validateIntakeTfn);

    return (
      <Fragment>
        <FlowCard title={tranche2Flow?.intake_form?.name || "Intake"}>
          {submitError && (
            <SubmitError message={submitError} details={submitErrorDetails} />
          )}
          <Survey model={intakeSurvey} />
        </FlowCard>
      </Fragment>
    );
  }

  if (currentStep === "complete") {
    return (
      <Animate type="pop">
        <Row color="brand" title="Thanks. Your details have been submitted for review." />
      </Animate>
    );
  }

  if (currentStep === "biometric") {
    return (
      <Fragment>
        <div className="w-[96vw] max-w-[980px] px-2 sm:px-4">
          <Card className="w-full mx-auto" />
        </div>
      </Fragment>
    );
  }

  return (
    <Animate type="pop">
      <Row color="brand" title="Unable to load this session right now." />
    </Animate>
  );
}
